import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const DeleteAvailabilityModal = ({ availability, selectedSlot, calendar, onClose, getAuthHeaders }) => {
  // Generate time options - only full hours for fixed calendars
  const generateTimeOptions = () => {
    const options = [];
    const isFloating = calendar?.booking_type === 'floating';
    
    for (let hour = 0; hour < 24; hour++) {
      if (isFloating) {
        // For floating: generate 00, 15, 30, 45 minutes for each hour
        ['00', '15', '30', '45'].forEach(minute => {
          const timeValue = `${hour.toString().padStart(2, '0')}:${minute}`;
          options.push(timeValue);
        });
      } else {
        // For fixed: only hourly (no minutes)
        const timeValue = `${hour.toString().padStart(2, '0')}:00`;
        options.push(timeValue);
      }
    }
    
    return options;
  };

  // Calculate default end time (1 hour after start time)
  const calculateDefaultEndTime = (startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const endHours = (hours + 1) % 24;
    const isFloating = calendar?.booking_type === 'floating';
    
    if (isFloating) {
      return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else {
      return `${endHours.toString().padStart(2, '0')}:00`;
    }
  };

  // Normalize time to valid slot based on calendar type
  const normalizeTime = (time) => {
    if (!time) return '09:00';
    const [hours, minutes] = time.split(':').map(Number);
    const isFloating = calendar?.booking_type === 'floating';
    
    if (isFloating) {
      // Round to nearest 15-min interval
      const roundedMinutes = Math.round(minutes / 15) * 15;
      if (roundedMinutes === 60) {
        return `${((hours + 1) % 24).toString().padStart(2, '0')}:00`;
      }
      return `${hours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
    } else {
      // Round to nearest hour
      return `${hours.toString().padStart(2, '0')}:00`;
    }
  };

  // Use the clicked slot time, normalized to valid time unit
  const startTime = normalizeTime(selectedSlot?.time || '09:00');
  const [formData, setFormData] = useState({
    start_time: startTime,
    end_time: calculateDefaultEndTime(startTime),
    recurrence_type: 'none',
    recurrence_months: 1,
    full_office_hours: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setFormData({
      ...formData,
      start_time: newStartTime,
      end_time: formData.full_office_hours ? formData.end_time : calculateDefaultEndTime(newStartTime)
    });
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate that we have an availability record
      if (!availability || !availability.id) {
        setError('Unable to identify the availability slot to delete. Please try again.');
        setLoading(false);
        return;
      }

      const requestData = {
        date: selectedSlot.date,
        start_time: formData.full_office_hours ? '07:00' : formData.start_time,
        end_time: formData.full_office_hours ? '21:00' : formData.end_time,
        recurrence_type: formData.recurrence_type
      };

      // Add duration for recurring deletions
      if (formData.recurrence_type !== 'none') {
        requestData.recurrence_months = parseInt(formData.recurrence_months);
      }

      // Call delete API with parameters
      await axios.delete(
        `${API_BASE_URL}/availability/${availability.id}`,
        { 
          headers: getAuthHeaders(),
          data: requestData
        }
      );

      onClose();
    } catch (err) {
      console.error('Error deleting availability:', err);
      const errorDetail = err.response?.data?.detail;
      
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
        setError(errorMessages);
      } else if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else {
        setError('Failed to delete availability');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-red-600">Delete Availability</h2>
            <p className="text-gray-600 mt-1">
              Starting from {selectedSlot?.date}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleDelete} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.full_office_hours}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  full_office_hours: e.target.checked,
                  start_time: e.target.checked ? '07:00' : formData.start_time,
                  end_time: e.target.checked ? '21:00' : formData.end_time
                })}
                className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500 mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Full Office Hours (7 AM - 9 PM)
              </span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <select
                value={formData.start_time}
                onChange={handleStartTimeChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={formData.full_office_hours}
              >
                {generateTimeOptions().map(time => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <select
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                required
                disabled={formData.full_office_hours}
              >
                {generateTimeOptions()
                  .filter(time => time > formData.start_time)
                  .map(time => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recurrence Pattern
            </label>
            <select
              value={formData.recurrence_type}
              onChange={(e) => setFormData({ ...formData, recurrence_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="none">One-time only</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {formData.recurrence_type !== 'none' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (months)
              </label>
              <input
                type="number"
                value={formData.recurrence_months}
                onChange={(e) => setFormData({ ...formData, recurrence_months: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                min="1"
                max="12"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.recurrence_type === 'daily' 
                  ? 'Default: 1 month from start date' 
                  : 'Default: 3 months from start date'}
              </p>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2 text-sm">Warning:</h4>
            <ul className="list-disc list-inside space-y-1 text-xs text-yellow-700">
              <li>This will permanently remove the selected availability</li>
              <li>Calendar will show as unavailable for this time period</li>
              {formData.recurrence_type !== 'none' && (
                <li className="font-semibold">All instances within the duration will be deleted</li>
              )}
            </ul>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deleting...' : 'Delete Availability'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteAvailabilityModal;
